import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, AlertTriangle, FileWarning, UserX, Search, Filter,
  CheckCircle2, Clock, UserMinus, Briefcase, Settings as SettingsIcon, UserPlus
} from 'lucide-react';
import EmployeeOnboardModal from '@/components/EmployeeOnboardModal';

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  on_leave: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  inactive: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/50',
  terminated: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

const TYPE_BADGE = {
  employee: 'bg-blue-500/15 text-blue-400',
  freelancer: 'bg-amber-500/15 text-amber-400',
  vendor: 'bg-violet-500/15 text-violet-400',
  intern: 'bg-cyan-500/15 text-cyan-400',
};

export default function HRCommandCenter() {
  const { api, user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [filters, setFilters] = useState({
    department: '', role: '', workforce_type: '', status: '', incomplete_only: false,
  });
  const [search, setSearch] = useState('');
  const [showOnboard, setShowOnboard] = useState(false);
  const isAdmin = user?.role === 'master_admin' || user?.role === 'hr_manager';

  const load = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    api.get(`/hr/people?${qs.toString()}`).then(r => setData(r.data)).catch(console.error);
    if (isAdmin) api.get('/hr/alerts').then(r => setAlerts(r.data)).catch(console.error);
  };

  useEffect(() => { api.get('/hr/vocabularies').then(r => setVocab(r.data)); }, [api]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  if (!data) return <div className="text-zinc-500 text-sm py-8 text-center" data-testid="hr-loading">Loading HR Command Center...</div>;

  const filteredPeople = data.people.filter(p =>
    !search ||
    (p.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.role || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="hr-command-center">
      {/* Hero */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-heading font-bold text-white">HR Command Center</h1>
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-cyan-500/15 text-cyan-300 rounded">ERP-grade · v2</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">Person = Profile + Role + Access + Assignment + Status — enforced</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowOnboard(true)} data-testid="hr-onboard-btn"
              className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 rounded-sm text-xs text-white font-medium">
              <UserPlus className="w-3.5 h-3.5" /> Onboard Person
            </button>
            <button onClick={() => nav('/hr/vocabularies')} data-testid="hr-vocabularies-btn"
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 hover:border-zinc-700 hover:text-white">
              <SettingsIcon className="w-3.5 h-3.5" /> Manage Vocabularies
            </button>
          </div>
        )}
      </div>

      {/* Alerts row (admin/HR only) */}
      {isAdmin && alerts && (
        <div className="grid grid-cols-4 gap-3" data-testid="hr-alerts-row">
          <AlertCard
            icon={FileWarning} label="Incomplete Profiles" value={alerts.totals.incomplete}
            color="text-amber-400" bg="from-amber-500/10 to-amber-500/5 border-amber-500/30"
            onClick={() => setFilters(f => ({ ...f, incomplete_only: !f.incomplete_only }))}
            active={filters.incomplete_only}
            testid="alert-incomplete"
          />
          <AlertCard
            icon={UserX} label="Inactive w/ Access" value={alerts.totals.inactive_with_access}
            color="text-rose-400" bg="from-rose-500/10 to-rose-500/5 border-rose-500/30"
            testid="alert-inactive-access"
          />
          <AlertCard
            icon={AlertTriangle} label="NDA Missing" value={alerts.totals.nda_missing}
            color="text-orange-400" bg="from-orange-500/10 to-orange-500/5 border-orange-500/30"
            testid="alert-nda"
          />
          <AlertCard
            icon={Clock} label="Contracts Expiring (30d)" value={alerts.totals.expiring_contracts}
            color="text-fuchsia-400" bg="from-fuchsia-500/10 to-fuchsia-500/5 border-fuchsia-500/30"
            testid="alert-contracts"
          />
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-6 gap-2" data-testid="hr-stats-strip">
        <Stat label="Total" value={data.total} color="text-white" />
        <Stat label="Employees" value={data.by_type.employee} color="text-blue-400" />
        <Stat label="Freelancers" value={data.by_type.freelancer} color="text-amber-400" />
        <Stat label="Vendors" value={data.by_type.vendor} color="text-violet-400" />
        <Stat label="Active" value={data.by_status.active} color="text-emerald-400" />
        <Stat label="Inactive/Terminated" value={(data.by_status.inactive || 0) + (data.by_status.terminated || 0)} color="text-rose-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid="hr-filters">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <FilterPill value={filters.workforce_type} onChange={(v) => setFilters(f => ({ ...f, workforce_type: v }))}
          label="Type" options={vocab?.employment_types || []} testid="filter-type" />
        <FilterPill value={filters.status} onChange={(v) => setFilters(f => ({ ...f, status: v }))}
          label="Status" options={vocab?.lifecycle_statuses || []} testid="filter-status" />
        <FilterPill value={filters.department} onChange={(v) => setFilters(f => ({ ...f, department: v }))}
          label="Department" options={vocab?.departments || []} testid="filter-dept" />
        <FilterPill value={filters.role} onChange={(v) => setFilters(f => ({ ...f, role: v }))}
          label="Role" options={(vocab?.roles || []).map(r => r.key)} testid="filter-role" />
        <div className="flex-1 min-w-[12rem] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, or role..."
            data-testid="hr-search"
            className="w-full h-8 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none" />
        </div>
        {(Object.values(filters).some(v => v) || search) && (
          <button onClick={() => { setFilters({ department: '', role: '', workforce_type: '', status: '', incomplete_only: false }); setSearch(''); }}
            className="text-[10px] text-zinc-500 hover:text-white px-2" data-testid="filter-clear">Clear</button>
        )}
      </div>

      {/* People table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="hr-people-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800 text-[9px] text-zinc-500 uppercase">
              <th className="text-left px-3 py-2">Person</th>
              <th className="text-left px-2 py-2 w-32">Type</th>
              <th className="text-left px-2 py-2 w-36">Role</th>
              <th className="text-left px-2 py-2 w-32">Department</th>
              <th className="text-left px-2 py-2 w-28">Status</th>
              <th className="text-left px-2 py-2 w-28">Profile</th>
              <th className="text-right px-3 py-2 w-20">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/40">
            {filteredPeople.map(p => {
              const pc = p.profile_completion || { complete: true, score: 100 };
              return (
                <tr key={p.id} className="hover:bg-zinc-800/30" data-testid={`hr-row-${p.id}`}>
                  <td className="px-3 py-2.5">
                    <p className="text-xs text-zinc-200">{p.full_name || '—'}</p>
                    <p className="text-[10px] text-zinc-600">{p.email}</p>
                  </td>
                  <td className="px-2 py-2.5">
                    <span className={`text-[8px] px-1.5 py-0.5 rounded uppercase font-medium ${TYPE_BADGE[p.workforce_type] || 'bg-zinc-800 text-zinc-500'}`}>
                      {p.workforce_type || '—'}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-[10px] text-zinc-300">{p.display_role || p.role || '—'}</td>
                  <td className="px-2 py-2.5 text-[10px] text-zinc-400">{p.department || '—'}</td>
                  <td className="px-2 py-2.5">
                    <span className={`text-[8px] px-1.5 py-0.5 rounded uppercase font-medium border ${STATUS_COLORS[p.lifecycle_status] || STATUS_COLORS.active}`}>
                      {p.lifecycle_status}
                    </span>
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1 bg-zinc-800 rounded">
                        <div className={`h-1 rounded ${pc.score >= 100 ? 'bg-emerald-500' : pc.score >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`}
                          style={{ width: `${pc.score}%` }} />
                      </div>
                      <span className={`text-[9px] ${pc.complete ? 'text-emerald-400' : 'text-amber-400'}`}>{pc.score}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => nav(`/hr/people/${p.id}`)} data-testid={`hr-open-${p.id}`}
                      className="text-[10px] px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/30">
                      Open →
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredPeople.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-xs text-zinc-500">No people match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showOnboard && (
        <EmployeeOnboardModal api={api} onClose={() => setShowOnboard(false)} onCreated={load} />
      )}
    </div>
  );
}

const AlertCard = ({ icon: Icon, label, value, color, bg, onClick, active, testid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`bg-gradient-to-br ${bg} border rounded-sm p-3 text-left transition-all ${onClick ? 'hover:scale-[1.02] cursor-pointer' : 'cursor-default'} ${active ? 'ring-2 ring-amber-500' : ''}`}>
    <div className="flex items-center justify-between">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className={`text-2xl font-heading font-bold ${color}`}>{value}</span>
    </div>
    <p className="text-[10px] text-zinc-300 mt-1.5">{label}</p>
  </button>
);

const Stat = ({ label, value, color }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-center">
    <p className={`text-lg font-heading font-bold ${color}`}>{value}</p>
    <p className="text-[9px] text-zinc-500 uppercase">{label}</p>
  </div>
);

const FilterPill = ({ value, onChange, label, options, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
    className="h-7 px-2 bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 focus:outline-none">
    <option value="">{label}: any</option>
    {options.map(o => (<option key={o} value={o}>{o.replace(/_/g, ' ')}</option>))}
  </select>
);
